import DrawerView from '@app/src/components/Drawer/DrawerView'
import { AvatarSize } from '@app/src/components/Ui/Avatar'
import TextField from '@app/src/components/Ui/TextField'
import CompanyAvatar from '@app/src/pages/ResourceCollection/Collections/CellComponents/CompanyAvatar'
import type { SimpleOrganization } from '@app/src/types/organizations'
import { resolveCompanyLogoImageUrlFromFields } from '@app/src/utils/resolveCompanyLogoImageUrl'
import type { OrganizationSettings } from '@app/src/wf-constants'
import { Stack, Typography } from '@mui/material'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useCsmOrganizationSettings } from './useCsmOrganizationSettings'

type ContractedValueField = 'contractedUsers' | 'contractedSuppliers'

interface EditOrganizationContractedValueDrawerProps {
  closeDrawer: () => void
  organization: SimpleOrganization
  field: ContractedValueField
  title: string
  currentValueLabel: string
  setValueLabel: string
  saveLabel: string
}

/**
 * Shared drawer for editing a single contracted numeric value (e.g. contracted users or
 * contracted suppliers) on an organization's CSM settings. Callers pass the settings field to
 * patch along with the labels to display; the save/notification/close flow is handled by
 * {@link useCsmOrganizationSettings}.
 */
const EditOrganizationContractedValueDrawer: React.FC<EditOrganizationContractedValueDrawerProps> = ({
  closeDrawer,
  organization,
  field,
  title,
  currentValueLabel,
  setValueLabel,
  saveLabel,
}) => {
  const { watch, register, handleSubmit, errors, setValue } = useForm<OrganizationSettings>()
  const { formatMessage } = useIntl()
  const { settings, solutionType, save, isSaving } = useCsmOrganizationSettings(organization, {
    closeDrawer,
    resetForm: () => setValue(field, undefined),
  })

  const existingValue = settings?.[field]
  const newValue = watch(field)

  const onSubmit = async () => {
    await save({ [field]: Number(newValue) })
  }

  return (
    <DrawerView
      title={title}
      buttons={[
        {
          variant: 'contained',
          label: saveLabel,
          onClick: handleSubmit(onSubmit),
          disabled: isSaving || newValue === undefined || `${newValue}` === '',
        },
      ]}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack mx={3}>
          <Stack alignItems="center" mb={6}>
            <CompanyAvatar
              organizationName={organization.name}
              imageUrl={resolveCompanyLogoImageUrlFromFields({
                organizationImageUrl: organization.image?.url,
                websiteAddress: organization.websiteAddress,
              })}
              size={AvatarSize.XXL}
            />
            <Typography variant="h5" mt={2} color="textPrimary">
              {organization.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {solutionType}
            </Typography>
          </Stack>
          <Typography fontWeight="bold" gutterBottom variant="body1" color="textPrimary">
            {currentValueLabel}: {existingValue}
          </Typography>

          <Typography fontWeight="bold" gutterBottom variant="body1" color="textPrimary" mt={6}>
            {setValueLabel}
          </Typography>
          <TextField
            hoveringLabel
            inputRef={register({
              required: formatMessage({ id: 'form.validation.required' }),
              min: { value: 0, message: formatMessage({ id: 'form.validation.required' }) },
            })}
            fullWidth
            name={field}
            type="number"
            helperText={errors?.[field]?.message}
            onClear={(): void => setValue(field, '')}
          />
        </Stack>
      </form>
    </DrawerView>
  )
}

export default EditOrganizationContractedValueDrawer
